/**
 * Auth API + in-memory token store for the MD Portal.
 *
 * Token is held in memory (not localStorage) to reduce PHI-token exposure.
 * A 401 from any API call should redirect the app to the login screen.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:7860';
const AUTH_PREFIX = '/api/auth';

const TOKEN_STORAGE_KEY = 'md.auth.token';
const SUBJECT_STORAGE_KEY = 'md.auth.subject';
const REMEMBER_ME_KEY = 'md.auth.rememberMe';

let _shouldRemember = false;

// Client-side helper to verify JWT expiry
function _isJwtUnexpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload || typeof payload.exp !== 'number') return true;
    return payload.exp * 1000 > Date.now();
  } catch {
    return true;
  }
}

// Module-level hydration on load
const _isRemembered = typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(REMEMBER_ME_KEY) === 'true';
let _token = null;
let _subject = null; // { sub, role, scope }

if (_isRemembered) {
  try {
    const storedToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedToken && _isJwtUnexpired(storedToken)) {
      _token = storedToken;
      const rawSubject = window.localStorage.getItem(SUBJECT_STORAGE_KEY);
      if (rawSubject) {
        _subject = JSON.parse(rawSubject);
      }
      _shouldRemember = true;
    } else {
      // Clear expired
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      window.localStorage.removeItem(SUBJECT_STORAGE_KEY);
      window.localStorage.removeItem(REMEMBER_ME_KEY);
    }
  } catch (e) {
    // safe fallback
  }
}

export function setRememberMe(value) {
  _shouldRemember = value;
}

const _listeners = new Set();

export function getToken() {
  return _token;
}

export function getSubject() {
  return _subject;
}

export function isAuthenticated() {
  return Boolean(_token);
}

export function onAuthChange(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

function _notify() {
  for (const l of _listeners) {
    try {
      l({ token: _token, subject: _subject });
    } catch (e) {
      // ignore listener errors
    }
  }
}

export function setToken(token, subject = null) {
  _token = token;
  _subject = subject;

  if (typeof window !== 'undefined' && window.localStorage) {
    if (_shouldRemember && token) {
      window.localStorage.setItem(REMEMBER_ME_KEY, 'true');
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
      if (subject) {
        window.localStorage.setItem(SUBJECT_STORAGE_KEY, JSON.stringify(subject));
      } else {
        window.localStorage.removeItem(SUBJECT_STORAGE_KEY);
      }
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      window.localStorage.removeItem(SUBJECT_STORAGE_KEY);
      window.localStorage.removeItem(REMEMBER_ME_KEY);
    }
  }
  _notify();
}

export function clearAuth() {
  _token = null;
  _subject = null;
  _shouldRemember = false;
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(SUBJECT_STORAGE_KEY);
    window.localStorage.removeItem(REMEMBER_ME_KEY);
  }
  _notify();
}

export function authHeaders(extra = {}) {
  return _token ? { ...extra, Authorization: `Bearer ${_token}` } : { ...extra };
}

/**
 * Handle a fetch Response, redirecting to login on 401.
 */
export function handleAuthError(response) {
  if (response.status === 401) {
    clearAuth();
  }
}

/**
 * Request a verification code to email or phone.
 */
export async function requestCode(contact, method) {
  const response = await fetch(`${API_BASE_URL}${AUTH_PREFIX}/request-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contact, method }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Could not send code (${response.status})`);
  }
  return response.json();
}

/**
 * Verify a code. On a single-context success, stores the token.
 * Returns the raw response so callers can handle disambiguation.
 */
export async function verifyCode(challengeId, code) {
  const response = await fetch(`${API_BASE_URL}${AUTH_PREFIX}/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge_id: challengeId, code }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Verification failed (${response.status})`);
  }
  const data = await response.json();
  if (data.token) {
    setToken(data.token, { role: data.role, patients: data.patients });
  }
  return data;
}

/**
 * Exchange a selection token for a scoped access token (disambiguation).
 */
export async function selectContext(selectionToken, patientId) {
  const response = await fetch(
    `${API_BASE_URL}${AUTH_PREFIX}/select-context?patient_id=${encodeURIComponent(patientId)}`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${selectionToken}` },
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Selection failed (${response.status})`);
  }
  const data = await response.json();
  if (data.token) {
    setToken(data.token, { role: data.role, patient_id: data.patient_id });
  }
  return data;
}

/**
 * Fetch the current subject (role/scope) for portal bootstrap.
 */
export async function fetchMe() {
  const response = await fetch(`${API_BASE_URL}${AUTH_PREFIX}/me`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    handleAuthError(response);
    throw new Error(`Auth check failed (${response.status})`);
  }
  const data = await response.json();
  _subject = data;

  if (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(REMEMBER_ME_KEY) === 'true') {
    window.localStorage.setItem(SUBJECT_STORAGE_KEY, JSON.stringify(_subject));
  }

  return data;
}
